import type { Session } from "@supabase/supabase-js";
import { ArchiveRestore, BarChart3, BookOpenText, CreditCard, History, Home, LogOut, Upload } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { AuthScreen } from "./components/AuthScreen";
import { OperationModal } from "./components/OperationModal";
import { createOperation, editOperation, getSession, loadWorkspace, onAuthChange, setTransactionDeleted, signOut } from "./lib/api";
import { isSupabaseConfigured } from "./lib/supabase";
import type { OperationDraft, WorkspaceData } from "./lib/types";
import { AccountsView } from "./views/AccountsView";
import { DashboardView } from "./views/DashboardView";
import { HistoricalImportView } from "./views/HistoricalImportView";
import { HistoryView } from "./views/HistoryView";
import { SummaryView } from "./views/SummaryView";

type View = "dashboard" | "summary" | "history" | "accounts" | "import" | "dictionary";

const NAVIGATION: Array<{ view: View; label: string; icon: typeof Home }> = [
  { view: "dashboard", label: "Обзор", icon: Home },
  { view: "summary", label: "Сводка", icon: BarChart3 },
  { view: "history", label: "История ДДС", icon: History },
  { view: "accounts", label: "Счета", icon: CreditCard },
  { view: "import", label: "Исторические данные", icon: Upload },
  { view: "dictionary", label: "Мой словарь", icon: BookOpenText },
];

const TITLES: Record<View, string> = {
  dashboard: "Мои деньги",
  summary: "Сводка",
  history: "История ДДС",
  accounts: "Счета и остатки",
  import: "Исторические данные",
  dictionary: "Мой словарь",
};

export function App() {
  if (!isSupabaseConfigured) return <SetupNotice />;
  return <ConfiguredApp />;
}

function ConfiguredApp() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [view, setView] = useState<View>("dashboard");
  const [data, setData] = useState<WorkspaceData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ value: OperationDraft; transactionId?: string } | null>(null);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    void getSession().then(setSession).catch((caught) => setError(caught instanceof Error ? caught.message : "Не удалось проверить сессию"));
    return onAuthChange(setSession);
  }, []);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!session?.user.id) return;
    try {
      setData(await loadWorkspace(session.user.id));
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось загрузить данные");
    }
  }, [session?.user.id]);

  useEffect(() => { void refresh(); }, [refresh]);

  const initials = useMemo(() => (session?.user.email?.slice(0, 2) ?? "M1").toLocaleUpperCase("ru"), [session?.user.email]);
  const dateLabel = new Intl.DateTimeFormat("ru-RU", { weekday: "long", day: "numeric", month: "long" }).format(new Date());

  async function confirmOperation(nextDraft: OperationDraft, transactionId?: string) {
    if (transactionId) await editOperation(transactionId, nextDraft);
    else await createOperation(nextDraft);
    await refresh();
    setDraft(null);
  }

  async function toggleDeleted(transactionId: string, deleted: boolean) {
    const verb = deleted ? "Удалить операцию? Остатки и аналитика будут пересчитаны." : "Восстановить операцию и снова учесть её в остатках?";
    if (!confirm(verb)) return;
    try {
      await setTransactionDeleted(transactionId, deleted);
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось изменить операцию");
    }
  }

  function navigate(next: View) {
    setView(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (session === undefined) return <LoadingScreen label="Проверяю вход…" />;
  if (!session) return <AuthScreen />;

  return (
    <div className="app-shell">
      <aside className="sidebar" aria-label="Основная навигация">
        <button className="brand brand-button" type="button" onClick={() => navigate("dashboard")}><span className="brand-mark">M1</span><span>Finance</span></button>
        <nav className="side-nav">
          {NAVIGATION.map(({ view: target, label, icon: Icon }) => <button className={`nav-item ${view === target ? "is-active" : ""}`} type="button" key={target} onClick={() => navigate(target)}><span className="nav-icon"><Icon size={18} /></span><span>{label}</span></button>)}
        </nav>
        <div className="sidebar-foot"><span className={`status-dot ${online ? "" : "offline-dot"}`} /><span>{online ? "Данные синхронизированы" : "Офлайн · только просмотр"}</span></div>
      </aside>

      <main className="main-content">
        <header className="topbar">
          <div><p className="eyebrow">{dateLabel}</p><h1>{TITLES[view]}</h1></div>
          <div className="profile-actions"><span className={`network-pill ${online ? "online" : "offline"}`}>{online ? "онлайн" : "офлайн"}</span><button className="profile-button" type="button" onClick={() => void signOut()} title="Выйти"><span>{initials}</span><LogOut className="profile-logout" size={14} /></button></div>
        </header>

        {error && <div className="app-error"><span>{error}</span><button type="button" onClick={() => void refresh()}>Повторить</button></div>}
        {!data ? <LoadingScreen label="Загружаю ваши финансы…" embedded /> : (
          <>
            {view === "dashboard" && <DashboardView data={data} onDraft={(value) => setDraft({ value })} onNavigate={navigate} />}
            {view === "summary" && <SummaryView />}
            {view === "history" && <HistoryView transactions={data.transactions} accounts={data.accounts} categories={data.categories} incomeSources={data.incomeSources} onEdit={(transactionId, value) => setDraft({ value, transactionId })} onRefund={(value) => setDraft({ value })} onDelete={toggleDeleted} />}
            {view === "accounts" && <AccountsView accounts={data.accounts} onChanged={refresh} />}
            {view === "import" && <HistoricalImportView onChanged={refresh} />}
            {view === "dictionary" && <DictionaryPlaceholder />}
          </>
        )}
      </main>

      <nav className="mobile-nav" aria-label="Навигация на телефоне">
        <MobileNavItem active={view === "dashboard"} label="Обзор" icon={Home} onClick={() => navigate("dashboard")} />
        <MobileNavItem active={view === "history"} label="История" icon={History} onClick={() => navigate("history")} />
        <button className="mobile-action" type="button" onClick={() => { navigate("dashboard"); setTimeout(() => document.querySelector<HTMLTextAreaElement>("#quick-entry-input")?.focus(), 100); }} aria-label="Добавить операцию"><span>+</span></button>
        <MobileNavItem active={view === "summary"} label="Сводка" icon={BarChart3} onClick={() => navigate("summary")} />
        <MobileNavItem active={view === "accounts"} label="Счета" icon={CreditCard} onClick={() => navigate("accounts")} />
      </nav>

      {draft && data && <OperationModal initialDraft={draft.value} transactionId={draft.transactionId} accounts={data.accounts} categories={data.categories} incomeSources={data.incomeSources} onClose={() => setDraft(null)} onConfirm={confirmOperation} />}
    </div>
  );
}

function MobileNavItem({ active, label, icon: Icon, onClick }: { active: boolean; label: string; icon: typeof Home; onClick: () => void }) {
  return <button className={`mobile-nav-item ${active ? "is-active" : ""}`} type="button" onClick={onClick}><Icon size={18} /><small>{label}</small></button>;
}

function DictionaryPlaceholder() {
  return <section className="view is-visible"><div className="placeholder-panel panel"><span className="placeholder-icon"><BookOpenText /></span><h2>Персональный словарь сохранён в архитектуре</h2><p>Редактор словаря будет подключён вместе с рабочим текстовым мозгом. Финансовые операции уже не зависят от демонстрационной памяти браузера.</p><span className="scope-note"><ArchiveRestore size={16} /> Таблица и RLS готовы; пользовательский редактор не входит в текущую последовательность финансового ядра.</span></div></section>;
}

function LoadingScreen({ label, embedded }: { label: string; embedded?: boolean }) {
  return <div className={embedded ? "loading-panel panel" : "full-loading"}><span className="loading-spinner" />{label}</div>;
}

function SetupNotice() {
  return <main className="auth-page"><section className="auth-card setup-card panel"><span className="brand-mark">M1</span><p className="panel-label">Рабочее ядро готово к подключению</p><h1>Укажите проект Supabase</h1><p className="auth-copy">Скопируйте <code>.env.example</code> в <code>.env.local</code>, заполните URL и Publishable Key (<code>sb_publishable_...</code>), затем примените миграции из папки <code>supabase/migrations</code>.</p><div className="setup-steps"><span>1. Supabase CLI: <code>supabase db reset</code></span><span>2. Запуск: <code>pnpm dev</code></span><span>3. Откройте адрес на ПК или телефоне в одной сети</span></div><p className="scope-note">Статический <code>dist/</code> остаётся нетронутым эталоном prototype-v0.1.</p></section></main>;
}
